import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { useMyPermissions } from '@/hooks/useMyPermissions';
import { getApiErrorMessage } from '@/lib/api/apiError';
import { toast } from '@/lib/notify';
import { cn } from '@/lib/utils';

import { useSetVerifiedStatus } from '../api/useSetVerifiedStatus';
import {
  acceptsVerdict,
  isVerdictAllowed,
  type Verdict,
  verdictBlockedReason,
} from '../verdictPolicy';
import { RequestResubmissionModal, type ResubmissionStep } from './modals/RequestResubmissionModal';
import { type DecisionVerdict, SessionDecisionModal } from './modals/SessionDecisionModal';
import {
  originalMeta,
  VERIFIED_STATUS_ORDER,
  VerifiedStatusBadge,
  VerifiedStatusOption,
  VerifiedStatusPill,
  verifiedStatusLabel,
} from './statusAxisBadges';

/**
 * The Verified Status cell: the tenant admin's current verdict, and the menu to
 * change it. The only editable status in the row — Original Status is what our
 * system concluded and is never overwritten from here.
 *
 * The menu is PORTALLED to the body and positioned `fixed`, matching the designer's
 * `.sc-sessions-status-dropdown-portal`. That is not a style choice; it is the only
 * way the menu can be seen at all. The table lives in an `overflow-auto` wrapper, and
 * an absolutely-positioned menu inside a row is clipped by that wrapper — so on the
 * last few rows of the page the verdict list was cut off mid-way. Leaving the
 * scroll container removes the clipping, and along with it the z-index juggling the
 * in-flow version needed to stay above the sticky header and the rows below it.
 *
 * The cost of `fixed` is that the menu no longer moves with the row, so anything that
 * moves the row has to close it — see the listeners below.
 *
 * WHICH options appear, and whether a reason is demanded first, is decided by
 * `verdictPolicy` — the same rules the API enforces. Picking a verdict that needs a
 * reason opens the designer's dialog instead of saving immediately; everything else
 * saves straight from the menu, which is the whole point of having both.
 */

/** Height to reserve below the badge before flipping the menu upwards. */
const MENU_SPACE = 220;

interface MenuPosition {
  /** Viewport y of the badge's top edge (flipped up) or bottom edge (dropped down). */
  y: number;
  x: number;
  openAbove: boolean;
}

/**
 * Only the fields this control actually needs, rather than a full SessionDto — so the
 * sessions list (SessionDto) and the session detail header (SessionDetailDto) can share
 * one implementation instead of each growing its own verdict dropdown.
 *
 * `originalStatus` is required because every rule about what may be set depends on it.
 */
interface VerdictTarget {
  id: string;
  originalStatus: string;
  verifiedStatus: string;
  /** Optional label for the dialog's subtitle — a mobile number or reference id. */
  label?: string;
  /** The verified applicant name, for the dialog subtitle. Falls back to "Applicant". */
  applicantName?: string | null;
  /** Where a decision email would go, prefilled into the (not yet enabled) email field. */
  email?: string | null;
  /** This run's steps, for the resubmission dialog's check list. */
  steps?: ResubmissionStep[];
}

/** The three verdicts the Approve/Decline-shaped dialog handles. */
function isDecisionVerdict(value: Verdict | null): value is DecisionVerdict {
  return value === 'Approved' || value === 'Rejected' || value === 'ExceptionRequested';
}

export function VerifiedStatusCell({ session }: { session: VerdictTarget }) {
  const [position, setPosition] = useState<MenuPosition | null>(null);
  const [pendingVerdict, setPendingVerdict] = useState<Verdict | null>(null);
  const [modalError, setModalError] = useState<string | null>(null);
  const anchorRef = useRef<HTMLDivElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const setVerified = useSetVerifiedStatus();
  const hasPermission = useMyPermissions();
  const current = session.verifiedStatus || 'NotApplicable';
  const open = position !== null;

  // Only TenantAdmin and Admin hold Session.Reject; Developer and Viewer are
  // view-only roles, and without this they would get a badge that looks clickable
  // and answers 403.
  //
  // Computed here but acted on after the hooks below: returning early would make the
  // effects conditional, and hook order has to be identical on every render.
  const canDecide = hasPermission('Session.Reject');
  const blockedReason = verdictBlockedReason(session.originalStatus);

  // The run's own status, in the dialog subtitle's words and colour — the same map the
  // badges use, so the dialog cannot describe the run differently from the row behind it.
  const { label: originalLabel, color: originalColor } = originalMeta(session.originalStatus);

  const decisionTarget = {
    referenceId: session.label,
    applicantName: session.applicantName ?? 'Applicant',
    originalStatus: session.originalStatus,
    originalStatusLabel: originalLabel,
    originalStatusColor: originalColor,
    email: session.email,
  };

  const close = useCallback(() => setPosition(null), []);

  const closeDialog = useCallback(() => {
    setPendingVerdict(null);
    setModalError(null);
  }, []);

  // Everything that would leave the menu pointing at the wrong place closes it.
  // Scroll is listened for in the CAPTURE phase because scroll does not bubble: the
  // table scrolls inside its own container, and only a capturing listener on window
  // hears that. Registered only while open, so a page of rows is not each holding
  // four listeners at rest.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    // A click INSIDE the menu is not an outside click. Checked here rather than by
    // hanging a stopPropagation handler on the menu wrapper, which would make a
    // presentational div behave as though it were interactive.
    const onDocClick = (e: MouseEvent) => {
      if (menuRef.current?.contains(e.target as Node)) return;
      close();
    };
    document.addEventListener('click', onDocClick);
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('click', onDocClick);
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, close]);

  /**
   * Measures the badge and drops the menu below it, or flips it above when the badge
   * sits too near the bottom of the viewport for the list to fit.
   */
  function toggle() {
    if (open) {
      close();
      return;
    }
    const rect = anchorRef.current?.getBoundingClientRect();
    if (!rect) return;
    const openAbove = window.innerHeight - rect.bottom < MENU_SPACE;
    setPosition({
      y: openAbove ? rect.top : rect.bottom,
      x: rect.left,
      openAbove,
    });
  }

  /** Records a verdict. `positions` is only read by the API for a resubmission request. */
  function save(value: Verdict, note?: string, positions?: number[]) {
    setVerified.mutate(
      {
        sessionId: session.id,
        verifiedStatus: value,
        note: note ?? null,
        resubmitStepPositions: positions,
      },
      {
        onSuccess: () => {
          setPendingVerdict(null);
          setModalError(null);
          toast.success(
            value === 'NotApplicable'
              ? 'Decision cleared'
              : value === 'ResubmitRequested'
                ? 'Resubmission requested — the applicant can redo those checks'
                : `Marked as ${verifiedStatusLabel(value)}`,
          );
        },
        // The API enforces the same rules independently, so a refusal here means the two
        // disagreed — show what it actually said rather than a generic failure. In the
        // dialog it goes inline, where the admin can act on it without losing their text.
        onError: (error) => {
          const message = getApiErrorMessage(error);
          if (pendingVerdict) setModalError(message);
          else toast.error(message);
        },
      },
    );
  }

  function choose(value: Verdict) {
    close();

    // Re-picking a verdict the row already carries is a no-op — EXCEPT for a resubmission
    // request, which is an action rather than a state. Reopening a step and giving the run
    // more time is something an admin may legitimately need to do twice (the applicant
    // fixed the wrong thing, or ran out of time again), and treating it as "already set"
    // would leave them unable to ask a second time without first clearing the verdict.
    if (value === current && value !== 'ResubmitRequested') return;

    // Every verdict that changes what somebody believes about the run opens the dialog the
    // designer drew for it. Only clearing to NA saves straight from the menu — there is
    // nothing to choose a reason for when the decision is being removed.
    if (value !== 'NotApplicable') {
      setModalError(null);
      setPendingVerdict(value);
      return;
    }
    save(value);
  }

  // A run nobody has started carries no verdict — not "NA", which reads as a decision
  // somebody took. A dash matches how the Submitted and Verified date columns already
  // render "nothing here yet", and the tooltip says why rather than leaving the admin
  // to wonder where the control went.
  if (!acceptsVerdict(session.originalStatus)) {
    return (
      <span
        title={blockedReason ?? undefined}
        className="text-[length:var(--text-14)] text-[#B3B3B3]"
      >
        —
      </span>
    );
  }

  if (!canDecide) {
    return <VerifiedStatusPill status={current} />;
  }

  return (
    <div ref={anchorRef}>
      <VerifiedStatusBadge
        status={current}
        disabled={setVerified.isPending}
        onClick={(e) => {
          // Stop the document listener above from closing it in the same tick.
          e.stopPropagation();
          toggle();
        }}
      />
      {position &&
        createPortal(
          // Fixed to the viewport, offset 4px from the badge. When flipped up it is
          // anchored by its BOTTOM edge, so the menu grows away from the badge instead
          // of overlapping it — which a top-anchored menu would do, since its height
          // isn't known until it renders.
          <div
            className="fixed z-[9999]"
            style={
              position.openAbove
                ? { bottom: window.innerHeight - position.y + 4, left: position.x }
                : { top: position.y + 4, left: position.x }
            }
          >
            <div
              ref={menuRef}
              role="menu"
              className={cn(
                'flex w-52 flex-col rounded-xl border border-[#E5E7EB] bg-white py-1.5',
                'shadow-[0_4px_20px_rgba(0,0,0,0.12)]',
              )}
            >
              {VERIFIED_STATUS_ORDER.filter((value) =>
                // The menu lists only what this run can actually be given. An option the
                // API would refuse is left out entirely rather than shown greyed: a
                // disabled row invites the click it then has to explain.
                isVerdictAllowed(session.originalStatus, value),
              ).map((value) => (
                <VerifiedStatusOption
                  key={value}
                  value={value}
                  selected={value === current}
                  onSelect={() => choose(value)}
                />
              ))}
            </div>
          </div>,
          document.body,
        )}

      {/* Approve / Decline / Exception — the designer's Approve and Decline dialogs, one
          component in three accent colours. */}
      <SessionDecisionModal
        open={isDecisionVerdict(pendingVerdict)}
        verdict={isDecisionVerdict(pendingVerdict) ? pendingVerdict : null}
        target={decisionTarget}
        isSaving={setVerified.isPending}
        errorMessage={modalError}
        onClose={closeDialog}
        onSubmit={(note) => {
          if (isDecisionVerdict(pendingVerdict)) save(pendingVerdict, note);
        }}
      />

      {/* Resubmission — the designer's workflow-checks dialog, driven by this run's steps. */}
      <RequestResubmissionModal
        open={pendingVerdict === 'ResubmitRequested'}
        target={{
          referenceId: session.label,
          applicantName: session.applicantName ?? 'Applicant',
          originalStatusLabel: originalLabel,
          originalStatusColor: originalColor,
          steps: session.steps ?? [],
        }}
        isSaving={setVerified.isPending}
        errorMessage={modalError}
        onClose={closeDialog}
        // The chosen positions go to the API, which reopens exactly those steps and gives
        // the run a fresh window of the workflow's own Session Validity. Messaging the
        // applicant is still to come; the link they already have works again.
        onSubmit={(note, positions) => save('ResubmitRequested', note, positions)}
      />
    </div>
  );
}
